import {
  CallQueueCallStatusEnum,
  GetCallQueueCallsCountDocument,
  GetCallQueueCallsCountQuery,
  GetCallQueueCallsCountQueryVariables,
  useCallQueueCallSubscription,
} from '@/types/generated/graphql';
import { gql, Reference } from '@apollo/client';
import { useRef } from 'react';
import { WAITING_CALLS_LIST_ARGS } from '../queued-calls.constants';
import { revealQueuePanel, shouldAutoOpenQueuePanel } from '../queued-calls.helpers';

const QUEUED_CALL_FRAGMENT = gql`
  fragment QueuedCallItem on CallQueueCall {
    id
    callQueueId
    channelId
    friendlyName
    contactPhoneNumber
    channelPhoneNumber
    channelIdentity
    status
    enqueuedAt
    position
  }
`;

export function useQueuedCallsSubscription() {
  const waitingIds = useRef(new Set<number>());

  useCallQueueCallSubscription({
    onData: async ({ client, data }) => {
      const node = data?.data?.callQueueCall?.node;
      if (node?.id == null) return;

      const isWaiting = node.status === CallQueueCallStatusEnum.waiting;
      const isNewCaller = isWaiting && !waitingIds.current.has(node.id);
      if (isWaiting) waitingIds.current.add(node.id);
      else waitingIds.current.delete(node.id);
      const ref = isWaiting ? client.cache.writeFragment({ data: node, fragment: QUEUED_CALL_FRAGMENT }) : null;

      client.cache.modify({
        fields: {
          getCallQueueCalls(existing, { storeFieldName, readField }) {
            if (!storeFieldName.includes(WAITING_CALLS_LIST_ARGS) || !existing) return existing;

            const items: Reference[] = existing.data ?? [];
            const withoutCall = items.filter((item) => readField('id', item) !== node.id);

            if (!ref) return { ...existing, data: withoutCall };

            const isKnown = withoutCall.length !== items.length;
            return {
              ...existing,
              data: isKnown ? items.map((i) => (readField('id', i) === node.id ? ref : i)) : [...items, ref],
            };
          },
        },
      });

      try {
        const { data: countData } = await client.query<
          GetCallQueueCallsCountQuery,
          GetCallQueueCallsCountQueryVariables
        >({
          query: GetCallQueueCallsCountDocument,
          variables: { status: CallQueueCallStatusEnum.waiting },
          fetchPolicy: 'network-only',
        });
        const waitingCount = countData?.getCallQueueCallsCount?.data?.count ?? 0;

        if (shouldAutoOpenQueuePanel({ isWaiting, isNewCaller, waitingCount })) revealQueuePanel();
      } catch {
        // the badge simply keeps its last value; the next event or the panel's own fetch catches up
      }
    },
  });
}
